import React, { useLayoutEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuthentication } from '../../authentiction';
import { useDataFetcher } from '../../hooks';
import { Alert, Container } from 'react-bootstrap';
import AjaxErrorBoundary from '../../ajax/errorBoundary';
import ErrorList from '../../errorList';
import {
  Well,
  FormHeading,
  Form,
  FormGroup,
  ButtonContainer,
  SubmitButton,
} from '../../form';
import { TextArea } from '../../formikBindings';
import {
  NotificationModal,
  INotificationModalProps,
  useModal,
} from '../../modals';
import { ILicenseDetails } from './types';
import schema from './schema';
import updateLicense from './updateLicense';

const transformData = (data: ILicenseDetails): ILicenseDetails => ({
  ...data,
  expiryDate: new Date(data.expiryDate).toLocaleDateString('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }),
});

const config = { transformer: transformData };

const UpdateLicensePage: React.FC = () => {
  const licenseElement = useRef<HTMLTextAreaElement>(null);

  const { onLicenseUpdated } = useAuthentication();
  const { showModal, hideModal } = useModal();
  const history = useHistory();

  const { busy, errors, data: currentLicence } = useDataFetcher(
    'api/settings/license',
    config,
  );

  useLayoutEffect(() => {
    licenseElement.current?.focus();
  }, []);

  return (
    <Container fluid>
      <Well>
        <FormHeading title="Update License" backPath="/admin" />
        <AjaxErrorBoundary
          busy={busy}
          loadingText={`Loading questionnaire`}
          errors={errors}
        >
          <ErrorList errors={errors} />
          <Form
            initialValues={{ license: '' }}
            validationSchema={schema}
            promise={updateLicense}
            onSubmit={(response) => {
              onLicenseUpdated(response.data);

              const notificationProps: INotificationModalProps = {
                title: 'License Updated',
                message: 'Your license has been successfully updated',
                icon: ['far', 'check-circle'],
                kind: 'success',
                onRequestClose: () => {
                  hideModal();
                  history.push('/admin');
                },
              };

              showModal(NotificationModal, notificationProps);
            }}
          >
            <FormGroup
              name="license"
              label="Please enter your new license key"
              required
            >
              <TextArea ref={licenseElement} name="license" rows={5} />
            </FormGroup>
            <ButtonContainer>
              <SubmitButton>Update License</SubmitButton>
            </ButtonContainer>
          </Form>
        </AjaxErrorBoundary>
        {currentLicence && (
          <Alert className="mt-3" variant="info">
            <div>
              Currently licensed for {currentLicence.numberOfSites} site(s)
            </div>
            <div>Expires on {currentLicence.expiryDate}</div>
          </Alert>
        )}
      </Well>
    </Container>
  );
};

export default UpdateLicensePage;
